import { DATE_TIME_FORMAT, ROUTES } from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import { resolveCurrentWorkerIdByCompany } from "@lhd/hooks";
import { WorkerLogtimeData, WorkerLogtimeMonthlyRecord } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { Button, Spin } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { isNil, toNumber } from "lodash";
import { Dispatch, memo, SetStateAction, useMemo, useRef } from "react";
import { IoCaretBack } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { convertSecondsToHHMM, headerName } from "../until";
import { Styles } from "./styled";
import { HiOutlineExternalLink } from "react-icons/hi";

dayjs.extend(isSameOrAfter);

interface IProps {
  historyTrackingTimes?: WorkerLogtimeMonthlyRecord[];
  monthHistory?: string;
  setMonthHistory?: Dispatch<SetStateAction<string>>;
  isLoadingHistoryTrackingTimes?: boolean;
  canOpenAttendanceManagement?: boolean;
  workerLogtimesData?: WorkerLogtimeData;
  isMobileOrTablet?: boolean;
}

const ClockHistory = ({
  historyTrackingTimes,
  monthHistory,
  setMonthHistory,
  isLoadingHistoryTrackingTimes,
  canOpenAttendanceManagement,
  workerLogtimesData,
  isMobileOrTablet,
}: IProps) => {
  const navigate = useNavigate();
  const auth = useSelector(user);
  const companyId = getCompanyId();
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, companyId);
  const headerTableRef = useRef<HTMLDivElement>(null);
  const bodyParentTableRef = useRef<HTMLDivElement>(null);

  const histories = useMemo(() => {
    if (!historyTrackingTimes) return [];
    const currentMonth = dayjs().format("YYYY-MM");
    const isCurrentMonth = monthHistory === currentMonth;

    const records = isCurrentMonth
      ? historyTrackingTimes.filter((history) =>
          dayjs(history.date).isBefore(dayjs(), "day"),
        )
      : historyTrackingTimes;

    return records
      .slice()
      .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [historyTrackingTimes, monthHistory]);
  const historiesMobile = workerLogtimesData?.history || [];

  const moveToListPage = () => {
    if (!canOpenAttendanceManagement) return;
    window.open(
      `${ROUTES.TRACKING_TIME}${ROUTES.LIST}?isDisplayInNewTab=true`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const handleEditAttendance = (workDate: string) => {
    navigate(
      `${ROUTES.TRACKING_TIME}${ROUTES.DETAIL}?worker_id=${currentWorkerId}&date=${workDate}&mode=edit`,
    );
  };
  const handlePreviousMonth = () => {
    const previousMonth = dayjs(monthHistory)
      .subtract(1, "month")
      .format("YYYY-MM");
    setMonthHistory?.(previousMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = dayjs(monthHistory).add(1, "month").format("YYYY-MM");
    setMonthHistory?.(nextMonth);
  };
  const isDisabledNextMonth = useMemo(() => {
    const currentMonth = dayjs().format("YYYY-MM");
    return dayjs(monthHistory).isSameOrAfter(currentMonth);
  }, [monthHistory]);

  return (
    <Styles isMobileOrTablet={isMobileOrTablet}>
      {isMobileOrTablet ? (
        <>
          <h3 className="title">打刻履歴</h3>
          <div>
            {historiesMobile.map((history, index) => {
              const isOffDay = !history.has_logtime;
              return (
                <div className="item-clock-history" key={index}>
                  <div className="clock-date">
                    {dayjs(history.work_date).format(
                      DATE_TIME_FORMAT.DATE_JP_MD_DAY,
                    )}
                  </div>
                  <div className="clock-time">
                    {isOffDay ? (
                      "休日"
                    ) : (
                      <>
                        <p>
                          実働時間 –
                          {convertSecondsToHHMM(history.working_hours)}
                        </p>
                        <p>
                          休憩 ：{convertSecondsToHHMM(history.break_hours)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="change-month">
            <button
              onClick={handlePreviousMonth}
              disabled={isLoadingHistoryTrackingTimes}
            >
              <IoCaretBack
                fontSize={28}
                className={`${
                  isLoadingHistoryTrackingTimes ? "disabled" : ""
                } pre`}
              />
            </button>
            <span>
              {monthHistory && dayjs(monthHistory).format("YYYY年M月")}{" "}
            </span>
            <button
              onClick={handleNextMonth}
              disabled={isLoadingHistoryTrackingTimes || isDisabledNextMonth}
            >
              <IoCaretBack
                fontSize={28}
                className={`${
                  isLoadingHistoryTrackingTimes || isDisabledNextMonth
                    ? "disabled"
                    : ""
                } next`}
              />
            </button>
          </div>

          <div>
            <div ref={headerTableRef}>
              <table className="table-fixed">
                <ColGroup />
                <thead>
                  <tr className="border-bottom-tr">
                    {headerName.map((header, index) => (
                      <th key={index}>{header.title}</th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
            {isLoadingHistoryTrackingTimes ? (
              <div className="d-flex justify-center align-center mt-3 mb-3 h-100">
                <Spin />
              </div>
            ) : (
              <div
                ref={bodyParentTableRef}
                style={{
                  overflowY: "auto",
                  direction: "rtl",
                }}
              >
                <table
                  className="table-fixed"
                  style={{ borderTop: "none", direction: "ltr" }}
                >
                  <ColGroup />
                  <tbody>
                    {histories?.map((history, index) => {
                      return (
                        <tr key={index}>
                          <td className="clock-date">
                            <Button className="date-btn">
                              {dayjs(history.date).format(
                                DATE_TIME_FORMAT.DATE_JP_DAY,
                              )}
                            </Button>
                          </td>
                          <td className="text-center fs-16">
                            {history.total_working_hours > 0
                              ? `${toNumber(
                                  history.total_working_hours / 3600,
                                )?.toFixed(1)} 時間`
                              : "0 時間"}
                          </td>
                          <td className="text-center fs-16">
                            {!isNil(history?.first_checkin_time)
                              ? dayjs(history?.first_checkin_time).format(
                                  "HH:mm",
                                )
                              : "-"}
                          </td>
                          <td className="text-center fs-16">～</td>
                          <td className="text-center fs-16">
                            {!isNil(history?.last_checkout_time)
                              ? dayjs(history?.last_checkout_time).format(
                                  "HH:mm",
                                )
                              : "-"}
                          </td>
                          <td className="text-right">
                            <Button
                              className="attendance-modify-btn"
                              onClick={() => handleEditAttendance(history.date)}
                            >
                              勤怠修正
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <Button
            className="attendance-management-btn d-flex align-item-center justify-center gap-1"
            disabled={!canOpenAttendanceManagement}
            onClick={moveToListPage}
          >
            <span>勤怠管理画面</span> <HiOutlineExternalLink fontSize={20} />
          </Button>
        </>
      )}
    </Styles>
  );
};

export default ClockHistory;

const ColGroup = memo(() => {
  return (
    <colgroup>
      {headerName.map((header, index) => (
        <col
          key={index}
          style={{
            width: header.width,
          }}
        />
      ))}
    </colgroup>
  );
});

ColGroup.displayName = "ColGroup";

export { ColGroup };
